/**
 * Writing an imported statement into the ledger. Reading and mapping the file
 * happens in `lib/` — this only does the database side.
 */
import { run } from '../db'
import { uid } from '../lib/id'
import { round2 } from '../lib/money'
import { loadTransactions } from './transactions'

const INSERT = `INSERT INTO transactions
    (id, date, description, amount, payment_type, settled, created_at, updated_at)
  VALUES (?, ?, ?, ?, ?, ?, ?, ?)`

/**
 * Inserts parsed statement entries, then lets reconciliation link whatever
 * fulfils a plan. One transaction: a statement lands whole or not at all.
 *
 * The ids come back with the rows: whatever wants to label them afterwards —
 * the suggester's review, say — needs to know which rows it just made.
 *
 * `entry.unsettled` marks a row the bank has not finished with, which is stored
 * as such so the ledger can say so and so a later export can clear it.
 *
 * @param {{date: string, description: string, amount: number,
 *          type?: string, unsettled?: boolean}[]} entries
 * @returns {Promise<string[]>} the ids written, in the order they were given
 */
export async function importTransactions(entries) {
  if (!entries?.length) return []
  const now = new Date().toISOString()
  const ids = []
  await run('BEGIN')
  try {
    for (const entry of entries) {
      const id = uid('tx')
      ids.push(id)
      await run(INSERT, [
        id,
        entry.date,
        entry.description,
        round2(entry.amount),
        entry.type ?? '',
        entry.unsettled ? 0 : 1,
        now,
        now,
      ])
    }
    await run('COMMIT')
  } catch (err) {
    await run('ROLLBACK')
    throw err
  }
  await loadTransactions()
  return ids
}

/**
 * Brings unsettled rows up to date from the statement that settled them.
 *
 * The bank moves the date and lengthens the description when a card payment
 * clears, so the row already in the ledger is holding the authorisation and not
 * the payment. This replaces both and marks it settled — the transaction's own
 * id, and anything filed against it, are untouched.
 *
 * @param {{id: string, date: string, description: string, payment_type?: string}[]} rows
 * @returns {Promise<number>} how many were brought up to date
 */
export async function settleTransactions(rows) {
  const list = (rows ?? []).filter((row) => row?.id)
  if (!list.length) return 0
  const now = new Date().toISOString()
  await run('BEGIN')
  try {
    for (const row of list) {
      await run(
        `UPDATE transactions
            SET date = ?, description = ?, payment_type = ?, settled = 1, updated_at = ?
          WHERE id = ?`,
        [row.date, row.description, row.payment_type ?? '', now, row.id],
      )
    }
    await run('COMMIT')
  } catch (err) {
    await run('ROLLBACK')
    throw err
  }
  await loadTransactions()
  return list.length
}
